/**
 * Typed event catalog for Forge Stats.
 *
 * Naming: `<type>.<action>_<dim>` — Forge backend groups by `type` + `name`
 * only (not by `data.<field>`), so every chartable dimension is baked into
 * the event name as a suffix. `data` is forensic-only (per-event inspection,
 * not chartable).
 *
 * Privacy contract: never send player names, vote target ids, guess text,
 * secret words, raw room codes, Discord handles/snowflakes, or chat. The
 * test suite enforces a deny-list against `data` shapes at compile time.
 */
import type { Phase } from '@infiltre/shared';

type Primitive = string | number | boolean;
type EventPayload = Record<string, Primitive | undefined>;

export const EVENT_SCHEMA_VERSION = 3;

/* ------------------------------------------------------------------------- */
/*  Dimension axes (suffixes)                                                */
/* ------------------------------------------------------------------------- */

export type AnalyticsPlatform = 'web' | 'discord' | 'capacitor' | 'tauri';
export type AnalyticsMode = 'online' | 'standalone';
export type AnalyticsAuthSource = 'discord' | 'guest';
export type AnalyticsCreateSurface = 'home' | 'discord_activity' | 'deep_link';
export type AnalyticsJoinSurface = 'manual' | 'deep_link' | 'discord_activity';
export type AnalyticsCreateFailReason = 'validation' | 'network' | 'server' | 'unknown';
export type AnalyticsJoinFailReason =
  | 'not_found'
  | 'full'
  | 'in_progress'
  | 'unauthorized'
  | 'unknown';
export type AnalyticsLeaveReason = 'voluntary' | 'kicked' | 'closed' | 'navigated';
export type AnalyticsHostChangeReason = 'manual' | 'auto_disconnect' | 'auto_grace_expired';
export type AnalyticsAbandonReason =
  | 'left'
  | 'closed'
  | 'disconnected'
  | 'host_quit'
  | 'tab_hidden';
export type AnalyticsGameMode = 'infiltre_classic' | 'team_deathmatch';
export type AnalyticsReconnectFailReason = 'max_attempts' | 'auth' | 'room_gone';
export type AnalyticsAuthRejectReason = 'session_bad' | 'replaced';
/** Lifecycle tag for engine_error — disambiguates real bugs from boot/reconnect races. */
export type AnalyticsErrorLifecycle = 'boot' | 'during_reconnect' | 'steady';
/** Codes considered benign noise during a reconnect window (stale pending-queue replay). */
export type AnalyticsReplayStaleCode =
  | 'wrong_phase'
  | 'invalid_target'
  | 'not_a_member'
  | 'unknown_player';

/* ------------------------------------------------------------------------- */
/*  Event union — suffix-only emission (one event per chartable dimension)   */
/* ------------------------------------------------------------------------- */

type LifecycleEvent = {
  name: `session.opened_${AnalyticsPlatform}`;
  data: { coldStart: boolean; locale: string; appVersion: string };
};

type AuthEvent =
  | { name: `auth.acquire_started_${AnalyticsAuthSource}`; data: Record<string, never> }
  | { name: `auth.acquire_succeeded_${AnalyticsAuthSource}`; data: Record<string, never> }
  | { name: `auth.acquire_failed_${AnalyticsAuthSource}`; data: { reason: string } };

type SetupEvent =
  | { name: `setup.opened_${AnalyticsMode}`; data: Record<string, never> }
  | { name: `setup.dismissed_completed_${AnalyticsMode}`; data: Record<string, never> };

type RoomEvent =
  | {
      name: `room.create_requested_${AnalyticsCreateSurface}`;
      data: { gameMode: AnalyticsGameMode };
    }
  | {
      name: `room.create_succeeded_${AnalyticsCreateSurface}`;
      data: { roomId: string; platform: AnalyticsPlatform };
    }
  | {
      name: `room.create_failed_${AnalyticsCreateSurface}`;
      data: { reason: AnalyticsCreateFailReason };
    }
  | { name: `room.join_requested_${AnalyticsJoinSurface}`; data: Record<string, never> }
  | {
      name: `room.join_succeeded_${AnalyticsJoinSurface}`;
      data: { roomId: string; platform: AnalyticsPlatform };
    }
  | {
      name: `room.join_failed_${AnalyticsJoinSurface}`;
      data: { reason: AnalyticsJoinFailReason };
    }
  | {
      name: `room.host_changed_${AnalyticsHostChangeReason}`;
      data: { roomId: string; isMe: boolean };
    };

type LobbyEvent =
  | { name: 'lobby.seated'; data: { roomId: string; isHost: boolean; seatNumber: number } }
  | {
      name: 'lobby.start_clicked';
      data: { roomId: string; playerCount: number; lobbyMs: number };
    }
  | { name: `lobby.left_${AnalyticsLeaveReason}`; data: { roomId: string; phase: Phase } };

type MugshotEvent = {
  name: 'mugshot.configured';
  data: { mode: AnalyticsMode; roomId: string; changeCount: number; randomized: boolean };
};

type GameEvent =
  | {
      name: `game.started_${AnalyticsMode}`;
      data: { roomId: string; gameMode: AnalyticsGameMode; playerCount: number };
    }
  | { name: 'game.round_advanced'; data: { roomId: string; round: number; prevRound: number } }
  | {
      name: `game.ended_${AnalyticsMode}`;
      data: { roomId: string; rounds: number; durationMs: number; winner: string };
    }
  | {
      name: `game.abandoned_${AnalyticsAbandonReason}`;
      data: { phase: Phase; round: number; mode: AnalyticsMode };
    }
  | {
      name: 'game.engine_error';
      data: {
        code: string;
        phase: Phase | 'unknown';
        lifecycle: AnalyticsErrorLifecycle;
      };
    };

type DriverEvent =
  | { name: 'driver.reconnected'; data: { attempts: number; durationMs: number } }
  | { name: `driver.reconnect_failed_${AnalyticsReconnectFailReason}`; data: Record<string, never> }
  | {
      name: `driver.auth_rejected_${AnalyticsAuthRejectReason}`;
      data: { closeCode: number };
    }
  // Engine error rerouted from engine_error when it fired inside a reconnect
  // window — these are stale pending-queue replays, not real bugs.
  | {
      name: `driver.replay_stale_${AnalyticsReplayStaleCode}`;
      data: { phase: Phase | 'unknown' };
    };

export type AnalyticsEvent =
  | LifecycleEvent
  | AuthEvent
  | SetupEvent
  | RoomEvent
  | LobbyEvent
  | MugshotEvent
  | GameEvent
  | DriverEvent;

export type AnalyticsEventName = AnalyticsEvent['name'];

/* ------------------------------------------------------------------------- */
/*  Helpers                                                                  */
/* ------------------------------------------------------------------------- */

export const splitEventName = (fullName: AnalyticsEventName): { type: string; name: string } => {
  const dot = fullName.indexOf('.');
  return { type: fullName.slice(0, dot), name: fullName.slice(dot + 1) };
};

/** Drops `undefined` values — Forge `EventData` only accepts primitives. */
export const sanitizeData = (data: EventPayload): Record<string, Primitive> => {
  const out: Record<string, Primitive> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
};
